/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useState } from "react"
import { AddImageUniversal, ImageOutlined } from "@duxton-web/icons"
import { AddPhotosSheet } from "ecommerce/components/AddPhotosSheet"
import {
  OptionPickerSheet,
  PickerField,
} from "ecommerce/components/OptionPickerSheet"
import {
  Avatar,
  Grid,
  notification,
  Select,
  Tag,
  Typography,
  Upload,
  UploadFile,
} from "@duxton-web/ui"
import classNames from "classnames"

import {
  INSPECTION_OUTCOME,
  INSPECTION_OUTCOME_LABEL,
  INSPECTION_REJECTION_REASON,
  INSPECTION_REJECTION_REASON_LABEL,
  InspectionDecision,
  InspectionOutcome,
  InspectionRejectionReason,
  RETURN_MEDIA_MAX_PHOTOS_PER_ITEM,
  RETURN_REASON_LABEL,
  RETURN_REFUND_TEXT,
  ReturnRequestItem,
} from "ecommerce/constants/returnRefund"
import {
  DRAFT_PHOTO_UID_PREFIX,
  isInspectionDecisionComplete,
  isSupportedPhoto,
  stageNativePhoto,
  toRenderedPhotoList,
} from "ecommerce/utils/returnRefundHelpers"

import { isNativeBridgeAvailable } from "utils/nativeBridge"
import { isWebview } from "utils/webview"

import styles from "./ReturnInspectionItemCard.modules.css"

const { Text } = Typography

/*
 * Module-scope: built from constants that never change, so every card shares one
 * stable options reference instead of allocating a fresh array per render.
 */
const OUTCOME_OPTIONS = Object.values(INSPECTION_OUTCOME).map(outcome => ({
  value: outcome,
  label: INSPECTION_OUTCOME_LABEL[outcome],
}))

/*
 * No `optionRender` helper rows, unlike the review card: inspection's reasons are
 * self-explanatory one-liners with no helper copy in the design.
 */
const REASON_OPTIONS = Object.values(INSPECTION_REJECTION_REASON).map(
  reason => ({
    value: reason,
    label: INSPECTION_REJECTION_REASON_LABEL[reason],
  }),
)

/**
 * The draft id behind a tile, from whichever source carries it: a tile rendered
 * FROM the server holds it in its uid, while one this session uploaded holds it
 * on `response` for as long as the tile lives (Duxton stamps it there,
 * JSON-parsed — hence `String`). Undefined means nothing was staged, so there is
 * nothing to delete.
 */
const resolveRemovableAssetID = (file: UploadFile): string | undefined => {
  if (file.uid.startsWith(DRAFT_PHOTO_UID_PREFIX)) {
    return file.uid.slice(DRAFT_PHOTO_UID_PREFIX.length)
  }

  return file.response === undefined ? undefined : String(file.response)
}

interface ReturnInspectionItemCardProps {
  item: ReturnRequestItem
  decision?: InspectionDecision
  /**
   * Sends an UPDATER so two writes in one tick (a removal's tombstone, then the
   * `onChange` Duxton fires right after) compose instead of clobbering.
   */
  onChange(
    itemID: string,
    update: (previous?: InspectionDecision) => InspectionDecision,
  ): void
  /**
   * Stages one photo as a server-side draft the moment it is added. Resolves with
   * the staged assetID; a rejection drops the tile (nothing was staged for it).
   */
  onUploadPhoto(
    item: ReturnRequestItem,
    file: File,
    mediaSource?: string,
  ): Promise<string>
  /**
   * Deletes one server-staged draft photo. Resolves `false` when the server kept
   * it, so the optimistically hidden tile comes back.
   */
  onRemovePhoto(item: ReturnRequestItem, assetID: string): Promise<boolean>
}

/**
 * One returned item awaiting inspection: the buyer's claim for context, then the
 * merchant's outcome — approve the refund, or report an issue with the goods (which
 * requires inspection's own reason set and optionally the merchant's evidence).
 */
export const ReturnInspectionItemCard: FC<ReturnInspectionItemCardProps> = ({
  item,
  decision,
  onChange,
  onUploadPhoto,
  onRemovePhoto,
}) => {
  const isComplete = isInspectionDecisionComplete(decision)
  const isReportingIssue = decision?.outcome === INSPECTION_OUTCOME.reportIssue

  /*
   * In the app the file input cannot reach the camera or gallery, so the tile opens
   * the Add-photos sheet instead and the app's own picker (`pickImage`) supplies the
   * photo. Desktop keeps the plain input, where the OS picker already covers both.
   *
   * The BRIDGE gates it, not the webview alone — see the review card: without one,
   * the sheet's rows would do nothing against an input this mode has disabled.
   */
  const isNativePicker = isWebview() && isNativeBridgeAvailable()
  const [isAddPhotosOpen, setAddPhotosOpen] = useState(false)
  /*
   * Slots held by picks the app has not answered yet — see the review card: they
   * count against the cap so a second pick cannot start against a count the first
   * one has not landed in yet.
   */
  const [pendingNativePicks, setPendingNativePicks] = useState(0)

  /*
   * Below `md` the design replaces the dropdowns with a bottom-sheet radio list —
   * the same width gate as the review card (see there for why width, not
   * `isWebview()`).
   */
  const screens = Grid.useBreakpoint()
  const isNarrow = screens.md === false
  const [openSheet, setOpenSheet] = useState<"outcome" | "reason">()

  /*
   * The sheets only mount while narrow, so widening past `md` unmounts one without
   * clearing which was open. Narrowing again would then remount it with `open`, springing
   * a picker the merchant never asked for.
   */
  useEffect(() => {
    if (!isNarrow) {
      setOpenSheet(undefined)
    }
  }, [isNarrow])

  const handleOutcomeChange = (outcome: InspectionOutcome) =>
    /*
     * Switching away from the issue outcome drops the REASON, but keeps the photo
     * bookkeeping: the uploads are already server-side drafts, so forgetting them
     * here would un-block Confirm mid-upload and resurrect removed tiles.
     */
    onChange(item.id, previous =>
      outcome === INSPECTION_OUTCOME.reportIssue
        ? { ...previous, outcome }
        : {
            outcome,
            supportingPhotos: previous?.supportingPhotos,
            removedDraftAssetIDs: previous?.removedDraftAssetIDs,
          },
    )

  /*
   * The issue fields only render while `isReportingIssue`, so the outcome is already
   * `reportIssue` at runtime; restating it is what lets TypeScript narrow the
   * optional `decision` prop into a complete `InspectionDecision`.
   */
  /*
   * Photo bookkeeping lands ASYNCHRONOUSLY — an upload settling, a refused
   * removal rolling back — by which time the merchant may have chosen another
   * outcome. These writes therefore patch photos onto whatever the decision is NOW;
   * only a decision that does not exist yet defaults to the one that offers
   * photos at all.
   */
  const handlePhotoChange = (patch: Partial<typeof decision>) =>
    onChange(item.id, previous => ({
      ...(previous ?? { outcome: INSPECTION_OUTCOME.reportIssue }),
      ...patch,
    }))

  const handleIssueChange = (patch: Partial<InspectionDecision>) =>
    onChange(item.id, previous => ({
      ...previous,
      outcome: INSPECTION_OUTCOME.reportIssue,
      ...patch,
    }))

  /*
   * One source for the tiles, the counter and the cap: server-staged drafts plus
   * this session's in-flight uploads, minus what the merchant just removed.
   */
  const photos = toRenderedPhotoList(
    item.draftPhotos,
    decision?.supportingPhotos,
    decision?.removedDraftAssetIDs,
  )
  const remainingPhotoSlots =
    RETURN_MEDIA_MAX_PHOTOS_PER_ITEM - photos.length - pendingNativePicks

  /*
   * Shared with the review card — the two differ only in the decision a photo-first
   * write starts from.
   */
  const handleNativePhoto = (photo: UploadFile, mediaSource: string) =>
    stageNativePhoto<InspectionDecision>({
      photo,
      mediaSource,
      defaultDecision: { outcome: INSPECTION_OUTCOME.reportIssue },
      onChange: update => onChange(item.id, update),
      upload: (file, source) => onUploadPhoto(item, file, source),
    })

  /* One tile face, rendered by whichever element the picker mode calls for. */
  const addPhotoTileContent = (
    <>
      <AddImageUniversal className={styles.uploadIcon} />
      <Text className={styles.uploadLabel}>
        {RETURN_REFUND_TEXT.reviewAddPhotos}
      </Text>
      <Text className={styles.uploadCount}>
        {RETURN_REFUND_TEXT.reviewAddPhotosCount(
          photos.length,
          RETURN_MEDIA_MAX_PHOTOS_PER_ITEM,
        )}
      </Text>
    </>
  )

  return (
    <div className={styles.card}>
      <div className={styles.summary}>
        <Avatar
          shape="square"
          src={item.imageURL}
          alt={item.name}
          // Without a fallback, a missing imageURL paints an empty transparent box.
          icon={<ImageOutlined />}
          className={styles.thumbnail}
        />
        <div className={styles.summaryText}>
          <Text strong>
            {RETURN_REFUND_TEXT.reviewItemHeading(item.quantity, item.name)}
          </Text>
          <Text className={styles.metadata}>
            {[item.variant, item.price].filter(Boolean).join(" · ")}
          </Text>
        </div>
        <Tag
          bordered={false}
          className={classNames(
            styles.stateTag,
            isComplete ? styles.stateCompleted : styles.statePending,
          )}
        >
          {isComplete
            ? RETURN_REFUND_TEXT.reviewStateCompleted
            : RETURN_REFUND_TEXT.reviewStatePending}
        </Tag>
      </div>

      <div className={styles.details}>
        {item.reason && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>
              {RETURN_REFUND_TEXT.reviewReturnReason}
            </Text>
            <Text>
              {/* Server copy first, the FE map for mock-backed items. */}
              {item.reasonLabel ??
                RETURN_REASON_LABEL[item.reason] ??
                item.reason}
            </Text>
          </div>
        )}
        {item.buyerMessage && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>
              {RETURN_REFUND_TEXT.reviewBuyerMessage}
            </Text>
            <Text>{`“${item.buyerMessage}”`}</Text>
          </div>
        )}
        {!!item.evidencePhotos?.length && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>
              {RETURN_REFUND_TEXT.reviewEvidencePhotos}
            </Text>
            <div className={styles.photoRow}>
              {item.evidencePhotos.map((photo, index) => (
                <img
                  key={photo}
                  className={styles.photo}
                  src={photo}
                  alt={RETURN_REFUND_TEXT.reviewEvidencePhotoAlt(index + 1)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <label className={styles.fieldLabel} htmlFor={`inspection-${item.id}`}>
        {RETURN_REFUND_TEXT.reviewActionLabel}
        <Text type="danger" className={styles.required}>
          *
        </Text>
      </label>
      {isNarrow ? (
        <PickerField
          id={`inspection-${item.id}`}
          value={
            decision?.outcome
              ? INSPECTION_OUTCOME_LABEL[decision.outcome]
              : undefined
          }
          placeholder={RETURN_REFUND_TEXT.reviewActionPlaceholder}
          isOpen={openSheet === "outcome"}
          onOpen={() => setOpenSheet("outcome")}
        />
      ) : (
        <Select
          id={`inspection-${item.id}`}
          className={styles.select}
          placeholder={RETURN_REFUND_TEXT.reviewActionPlaceholder}
          value={decision?.outcome}
          options={OUTCOME_OPTIONS}
          onChange={handleOutcomeChange}
        />
      )}

      {isReportingIssue && (
        <>
          <label
            className={styles.fieldLabel}
            htmlFor={`inspection-reason-${item.id}`}
          >
            {RETURN_REFUND_TEXT.reviewReasonLabel}
            <Text type="danger" className={styles.required}>
              *
            </Text>
          </label>
          {isNarrow ? (
            <PickerField
              id={`inspection-reason-${item.id}`}
              value={
                decision?.reason
                  ? INSPECTION_REJECTION_REASON_LABEL[decision.reason]
                  : undefined
              }
              placeholder={RETURN_REFUND_TEXT.reviewReasonPlaceholder}
              isOpen={openSheet === "reason"}
              onOpen={() => setOpenSheet("reason")}
            />
          ) : (
            <Select
              id={`inspection-reason-${item.id}`}
              className={styles.select}
              placeholder={RETURN_REFUND_TEXT.reviewReasonPlaceholder}
              value={decision?.reason}
              options={REASON_OPTIONS}
              onChange={(reason: InspectionRejectionReason) =>
                handleIssueChange({ reason })
              }
            />
          )}

          <div className={styles.fieldLabel}>
            {RETURN_REFUND_TEXT.reviewSupportingDocument}
          </div>
          {/*
            Uploads on ADD (`uploadPurpose: INSPECTION` — the host binds it): an
            accepted file goes straight through `customRequest` and the server
            stages it as a draft the refetched detail echoes back in
            `item.draftPhotos`. `LIST_IGNORE` still drops anything the gate
            rejects before a request fires.
          */}
          <Upload
            listType="picture-card"
            openFileDialogOnClick={!isNativePicker}
            multiple
            accept="image/jpeg,image/png"
            /*
             * Server drafts the merchant has not touched, then this session's own
             * tiles — which keep rendering their own entry even after the echoed
             * draft arrives, so an upload never swaps one tile for another. The
             * local `UploadFile`s hand back as-is because the list caches each
             * thumbnail by mutating its file object.
             */
            fileList={photos}
            beforeUpload={(file, batch) => {
              // Type and size first, so an unsupported file always gets told why.
              if (!isSupportedPhoto(file)) {
                notification.error({
                  message: RETURN_REFUND_TEXT.reviewPhotoRejected(file.name),
                })

                return Upload.LIST_IGNORE
              }

              /*
               * A multi-select can overshoot the cap: keep what fits, in pick
               * order, and drop the rest rather than letting the server reject
               * them one by one. Only SUPPORTED predecessors count — a rejected
               * file takes no slot, so counting raw batch positions would drop a
               * valid photo that had one waiting for it.
               */
              const supportedBefore = batch
                .slice(0, batch.indexOf(file))
                .filter(candidate => isSupportedPhoto(candidate)).length

              return supportedBefore < remainingPhotoSlots || Upload.LIST_IGNORE
            }}
            customRequest={({ file, onSuccess, onError }) =>
              onUploadPhoto(item, file as File).then(
                assetID => onSuccess?.(assetID),
                error => {
                  // The tile disappears via the error filter in onChange; the
                  // toast is what tells the merchant to re-add it.
                  notification.error({
                    message: RETURN_REFUND_TEXT.photoUploadFailed(
                      (file as File).name,
                    ),
                  })
                  onError?.(error)
                },
              )
            }
            onRemove={file => {
              /*
               * An in-flight upload cannot be cancelled: `customRequest` returns a
               * plain promise, so rc-upload's abort is a no-op and the POST still
               * stages a draft — which would come back on the next refetch with
               * nothing to hide it. Refuse the removal until the tile settles.
               */
              if (file.status === "uploading") {
                return false
              }

              const assetID = resolveRemovableAssetID(file)

              if (assetID) {
                /*
                 * Hide first, then delete: the tombstone drops the tile straight
                 * away and the REMOVE call follows. A refusal puts it back — the
                 * draft still exists server-side and would still attach.
                 */
                onChange(item.id, previous => ({
                  ...(previous ?? { outcome: INSPECTION_OUTCOME.reportIssue }),
                  removedDraftAssetIDs: [
                    ...(previous?.removedDraftAssetIDs ?? []),
                    assetID,
                  ],
                }))
                onRemovePhoto(item, assetID).then(removed => {
                  if (!removed) {
                    onChange(item.id, previous => ({
                      ...(previous ?? {
                        outcome: INSPECTION_OUTCOME.reportIssue,
                      }),
                      removedDraftAssetIDs: (
                        previous?.removedDraftAssetIDs ?? []
                      ).filter(id => id !== assetID),
                    }))
                  }
                })
              }

              return true
            }}
            onChange={({ fileList }) =>
              handlePhotoChange({
                /*
                 * Only this session's local entries persist — server-draft tiles
                 * re-derive from `draftPhotos`, and a failed upload drops out here
                 * (its toast already asked for a re-add).
                 */
                supportingPhotos: fileList.filter(
                  file =>
                    !file.uid.startsWith(DRAFT_PHOTO_UID_PREFIX) &&
                    file.status !== "error",
                ),
              })
            }
          >
            {/* No add tile at the cap — antd hides it when there are no children. */}
            {remainingPhotoSlots > 0 &&
              (isNativePicker ? (
                /*
                 * A real BUTTON on the native path — see the review card:
                 * `openFileDialogOnClick={false}` makes rc-upload's own key handler
                 * a no-op, leaving a div focusable but impossible to activate.
                 */
                <button
                  type="button"
                  className={styles.uploadTile}
                  onClick={() => setAddPhotosOpen(true)}
                >
                  {addPhotoTileContent}
                </button>
              ) : (
                <div className={styles.uploadTile}>{addPhotoTileContent}</div>
              ))}
          </Upload>
          {isNativePicker && (
            <AddPhotosSheet
              open={isAddPhotosOpen}
              onClose={() => setAddPhotosOpen(false)}
              onPhoto={handleNativePhoto}
              onPickingChange={isPicking =>
                setPendingNativePicks(count => count + (isPicking ? 1 : -1))
              }
            />
          )}
        </>
      )}

      {isNarrow && (
        <>
          <OptionPickerSheet
            open={openSheet === "outcome"}
            title={RETURN_REFUND_TEXT.actionSheetTitle}
            options={OUTCOME_OPTIONS}
            value={decision?.outcome}
            onCancel={() => setOpenSheet(undefined)}
            onSelect={value => {
              handleOutcomeChange(value as InspectionOutcome)
              setOpenSheet(undefined)
            }}
          />
          <OptionPickerSheet
            open={openSheet === "reason"}
            title={RETURN_REFUND_TEXT.reviewReasonLabel}
            options={REASON_OPTIONS}
            value={decision?.reason}
            onCancel={() => setOpenSheet(undefined)}
            onSelect={value => {
              handleIssueChange({
                reason: value as InspectionRejectionReason,
              })
              setOpenSheet(undefined)
            }}
          />
        </>
      )}
    </div>
  )
}
